// Side-panel row editor (insert or update). Reads + writes
// `root._extEditor`. Save/delete flow through saveRemoteRow /
// deleteRemoteRow. Opens when `_extEditor` is non-null.

export const RemoteRowField = {
  flow: 'y',
  gap: 'V',
  // FA204: read by the boolean choice's chips via `el.scope` — a module-scope
  // helper would read as undefined once frank serializes the component.
  scope: {
    boolValue(s) {
      const v = s.root?._extEditor?.values?.[s.fieldKey]
      if (v === true || v === 'true') return 'true'
      if (v === false || v === 'false') return 'false'
      return ''
    }
  },

  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => {
      const col = s.col || {}
      const parts = [col.name]
      if (col.is_primary_key) parts.push('(PK)')
      if (!col.nullable) parts.push('*')
      return parts.join(' ')
    }
  },

  TypeHint: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'caption',
    text: (el, s) => {
      const col = s.col || {}
      return col.format ? `${col.type} · ${col.format}` : col.type || ''
    }
  },

  Input: {
    hide: (el, s) => {
      const col = s.col || {}
      return col.type === 'boolean' || col.format === 'jsonb' || col.format === 'json'
    },
    tag: 'input',
    type: 'text',
    width: '100%',
    theme: 'field',
    minHeight: 'controlH',
    round: 'radiusControl',
    color: 'title',
    fontFamily: 'mono',
    fontSize: 'Z2',
    padding: '0 A',
    disabled: (el, s) => {
      const ed = s.root?._extEditor
      const col = s.col || {}
      return ed?.mode === 'update' && col.is_primary_key
    },
    placeholder: (el, s) => {
      const col = s.col || {}
      if (col.format === 'uuid') return '00000000-0000-0000-0000-000000000000'
      if (col.format === 'date-time') return '2026-01-01T00:00:00Z'
      return col.nullable ? '(null)' : ''
    },
    value: (el, s) => {
      const ed = s.root?._extEditor
      return ed?.values?.[s.fieldKey] ?? ''
    },
    onInput: (e, el, s) => {
      const ed = s.root?._extEditor
      if (!ed) return
      s.root.update({
        _extEditor: {
          ...ed,
          values: { ...(ed.values || {}), [s.fieldKey]: e.target.value }
        }
      })
    }
  },

  // A boolean column is a 3-way CHOICE (empty / true / false), so it is a
  // segmented control, not a native select (brief §4.4, 2026-09-24). Its
  // value lives in the editor draft on root, so the chips carry that wiring;
  // the look is AppSegmented's (the brand TabSet with the D-1 inversion).
  AppSegmented: {
    hide: (el, s) => s.col?.type !== 'boolean',
    ariaLabel: (el, s) => s.col?.name || '',
    options: [
      { value: '', label: '—' },
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' }
    ],
    childProps: {
      isActive: (el, s) => el.scope.boolValue(s) === el.optionValue,
      ariaPressed: (el, s) => (el.scope.boolValue(s) === el.optionValue ? 'true' : 'false'),
      onClick: (e, el, s) => {
        const ed = s.root?._extEditor
        if (!ed) return
        s.root.update({
          _extEditor: {
            ...ed,
            values: { ...(ed.values || {}), [s.fieldKey]: el.optionValue }
          }
        })
      }
    }
  },

  JsonArea: {
    hide: (el, s) => {
      const col = s.col || {}
      return col.format !== 'jsonb' && col.format !== 'json'
    },
    tag: 'textarea',
    rows: '4',
    width: '100%',
    theme: 'field',
    minHeight: 'controlH',
    round: 'radiusControl',
    color: 'title',
    fontFamily: 'mono',
    fontSize: 'Y2',
    padding: 'Z',
    placeholder: '{} or []',
    value: (el, s) => {
      const ed = s.root?._extEditor
      return ed?.values?.[s.fieldKey] ?? ''
    },
    onInput: (e, el, s) => {
      const ed = s.root?._extEditor
      if (!ed) return
      s.root.update({
        _extEditor: {
          ...ed,
          values: { ...(ed.values || {}), [s.fieldKey]: e.target.value }
        }
      })
    }
  }
}

export const RemoteRowEditor = {
  hide: (el, s) => !s.root?._extEditor,
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  background: 'scrim',
  zIndex: '100',
  align: 'flex-start flex-end',
  onClick: (e, el, s) => {
    if (e.target === el.node) s.root.update({ _extEditor: null })
  },

  Panel: {
    width: '520px',
    height: '100%',
    // Fill-contrast sheet (was a left-edge `line` border — border-habit
    // sweep, design-direction.md §8) — the panel's own opaque `document`
    // fill + `overlay` elevation shadow already separate it from the page.
    background: 'document',
    overflow: 'auto',
    flow: 'y',
    shadow: 'overlay',

    Header: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A',
      // Bottom divider removed (border-habit sweep, design-direction.md §8)
      // — padding + typographic hierarchy already separate Header from Body,
      // same precedent as AiActionConfirmCard.
      Title: {
        flow: 'y',
        gap: 'V',
        H2: {
          tag: 'h2',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          margin: '0',
          text: (el, s) =>
            el.call(
              'polyglot',
              s.root?._extEditor?.mode === 'insert'
                ? 'editor.remote.newRow'
                : 'editor.remote.editRow'
            )
        },
        Meta: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) => {
            const ed = s.root?._extEditor
            if (!ed) return ''
            return `${ed.slug} · ${ed.table}`
          }
        }
      },
      // THE house close X (brand CloseButton — F2, one X for every panel).
      CloseBtn: {
        extends: 'CloseButton',
        onClick: (e, el, s) => s.root.update({ _extEditor: null })
      }
    },

    Body: {
      padding: 'A',
      flow: 'y',
      gap: 'Z1',
      Fields: {
        flow: 'y',
        gap: 'Z1',
        childExtends: 'RemoteRowField',
        childrenAs: 'state',
        children: (el, s) => {
          const ed = s.root?._extEditor
          if (!ed) return []
          const schema = el.call('schemaForRemoteTable', s.root, ed.slug, ed.table)
          return (schema?.columns || []).map((c) => ({ col: c, fieldKey: c.name }))
        }
      }
    },

    Footer: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A',
      gap: 'Z',
      // Top divider removed (border-habit sweep, design-direction.md §8) —
      // Footer's own `overlay` fill already separates it from Body.
      background: 'overlay',
      position: 'sticky',
      bottom: '0',

      DeleteBtn: {
        tag: 'button',
        type: 'button',
        hide: (el, s) => s.root?._extEditor?.mode !== 'update',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        // Ghost fill (was transparent + `line` outline — the outlined-button
        // habit, border-habit sweep). Rest = overlay, hover = the danger
        // tint (§8.1 state matrix: danger → red ink, hover red.1).
        background: 'overlay',
        color: 'red',
        ':hover': { background: 'red.1' },
        fontSize: 'Z2',
        cursor: 'pointer',
        text: '{{ editor.remote.deleteRow | polyglot }}',
        onClick: async (e, el, s) => {
          if (!window.confirm(el.call('polyglot', 'editor.remote.confirmDelete'))) return
          const res = await el.call('deleteRemoteRow', s.root)
          if (res?.ok) {
            el.call('openNotification', {
              title: el.call('polyglot', 'editor.remote.rowDeleted'),
              type: 'success'
            })
          } else {
            el.call('openNotification', {
              title: el.call('polyglot', 'editor.remote.deleteFailed'),
              message: res?.error,
              type: 'error'
            })
          }
        }
      },

      Right: {
        flow: 'x',
        gap: 'Z',
        CancelBtn: {
          tag: 'button',
          type: 'button',
          minHeight: 'controlH',
          padding: '0 A',
          round: 'radiusPill',
          // Ghost fill (was transparent + `line` outline — the
          // outlined-button habit, border-habit sweep). Rest = overlay,
          // hover = the house hover wash.
          background: 'overlay',
          color: 'title',
          ':hover': { background: 'hover' },
          fontSize: 'Z2',
          cursor: 'pointer',
          text: '{{ common.action.cancel | polyglot }}',
          onClick: (e, el, s) => s.root.update({ _extEditor: null })
        },
        SaveBtn: {
          tag: 'button',
          type: 'button',
          minHeight: 'controlH',
          padding: '0 A',
          round: 'radiusPill',
          // Reviewed (fixed-colour sweep): kept — fixed brand-accent CTA,
          // mirrors CancelBtn's sibling (adaptive `title` on `overlay`);
          // this button's own backing never adapts.
          background: 'blue',
          color: 'fillInk',
          fontSize: 'Z2',
          fontWeight: '700',
          cursor: 'pointer',
          border: 'none',
          text: (el, s) =>
            el.call(
              'polyglot',
              s.root?._extEditorSaving ? 'common.state.saving' : 'common.action.save'
            ),
          onClick: async (e, el, s) => {
            const res = await el.call('saveRemoteRow', s.root)
            if (res?.ok) {
              el.call('openNotification', {
                title: el.call('polyglot', 'common.state.saved'),
                type: 'success'
              })
            } else {
              el.call('openNotification', {
                title: el.call('polyglot', 'editor.remote.saveFailed'),
                message: res?.error,
                type: 'error'
              })
            }
          }
        }
      }
    }
  }
}
